import { PersonAvatarVariant } from '@factorialco/f0-react'
import { ExternalLink, Pencil, Plus, Settings } from '@factorialco/f0-react/icons/app'
import { PostsGroupHeaderQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type Props = {
  group: PostsGroupHeaderQuery['posts']['group']
  isDynamicCommunitiesEnabled?: boolean
}

export const buildCommunityHeaderProps = ({
  group,
  isDynamicCommunitiesEnabled = false,
}: Props) => {
  if (!group) {
    return {
      title: '',
    }
  }

  const isDefaultGroup = group.default
  const canLeaveGroup = group._policies?.leave?.can && !isDefaultGroup
  const canUpdateGroup = group._policies?.update.can && group.isMember
  const canManageMembers = canUpdateGroup && !isDefaultGroup
  const canDeleteGroup = canUpdateGroup && !isDefaultGroup

  const secondaryActions = [
    canUpdateGroup && {
      label: i18n.t('posts.communities.edit_group'),
      icon: Pencil,
      key: 'edit-community',
    },
  ].filter(Boolean)

  const otherActions = [
    canManageMembers && {
      label: i18n.t('posts.communities.add_members'),
      key: 'add-members',
    },
    canManageMembers && {
      label: i18n.t('posts.communities.remove_members'),
      key: 'remove-members',
    },
    canLeaveGroup && {
      label: i18n.t('posts.communities.leave_group'),
      key: 'leave-group',
    },
    canDeleteGroup && {
      label: i18n.t('posts.communities.delete_group'),
      key: 'delete-community',
      critical: true,
    },
  ].filter(Boolean)

  const primaryActionItems = [
    {
      label: i18n.t('posts.communities.invite_people'),
      value: 'invite-people',
    },
    isDynamicCommunitiesEnabled && {
      label: i18n.t('posts.communities.set_automatic_invites'),
      value: 'set-automatic-invites',
      icon: Settings,
      description: i18n.t('posts.communities.set_automatic_invites_description'),
    },
  ].filter(Boolean)

  let primaryAction = null

  if (!isDefaultGroup) {
    if (!group.isMember) {
      primaryAction = {
        label: i18n.t('dashboard.groups.join.submit'),
        icon: Plus,
        key: 'join-group',
      }
    } else if (canUpdateGroup && primaryActionItems.length > 1) {
      primaryAction = {
        items: primaryActionItems,
        key: 'primary-dropdown',
      }
    } else if (canUpdateGroup) {
      primaryAction = {
        label: i18n.t('posts.communities.invite_people'),
        icon: Plus,
        key: 'invite-people',
      }
    }
  }

  const allMembersAvatars: PersonAvatarVariant[] = group.employees.map((employee) => ({
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    type: 'person' as const,
    src: buildAvatarUrl(employee.avatar),
  }))

  const metadata =
    allMembersAvatars.length > 0
      ? [
          {
            label: i18n.t('posts.communities.members'),
            value: {
              type: 'list' as const,
              variant: 'person' as const,
              avatars: allMembersAvatars,
            },
            actions: [
              {
                label: i18n.t('posts.communities.members'),
                icon: ExternalLink,
              },
            ],
          },
        ]
      : []

  return {
    title: group.title ?? '',
    description: group.description && group.description !== '-' ? group.description : '',
    status: {
      label: '',
      text: i18n.t('posts.communities.public'),
      variant: 'neutral' as const,
    },
    metadata,
    primaryAction,
    secondaryActions,
    otherActions,
  }
}
